import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FilterForm } from './funnel-filter.interface';
import { isBoolean } from '../../helpers';
import { CrmService } from '../../services/crm.service';
import { isNumber } from 'lodash';
import { MatMenuTrigger } from '@angular/material/menu';
import { combineLatest } from 'rxjs';
import { FUNNEL_TYPE, activeTranslations } from '../../enum/constants';
import { ModalService } from '../../services/modal.service';
import { AddFieldFilterComponent } from '../modals/add-field-filter/add-field-filter.component';
import { DialogService } from '../../services/dialog.service';
import { NotificationService } from '../../services/notifications.service';
import { ActivatedRoute } from '@angular/router';
import { DropdownItem } from '../dropdown-v2/dropdown-v2.interface';

interface FieldSetting {
  title: string;
  key: string;
  type: string;
  checked: boolean;
}

@Component({
  selector: 'app-funnel-filter',
  templateUrl: './funnel-filter.component.html',
  styleUrls: ['./funnel-filter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FunnelFilterComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FunnelFilterComponent implements OnInit, ControlValueAccessor {
  @Input() searchType: string = 'transparent';
  @Input() label: string = '';
  @Input() customClass: string = '';
  // @Input() placeholder: string = 'Пошук...';
  @Input() disabled: boolean = false;
  @Input() autoFocus: boolean = false;
  @Input() input: string = '';
  @Input() filter: any = {};
  @Input() customFields: any = [];
  @Output() onChangeClick = new EventEmitter();
  @Output() onFilterSearch = new EventEmitter();
  @Output() onChangeSave = new EventEmitter();
  @Output() onChangeCancel = new EventEmitter();
  @Output() onValueChange = new EventEmitter();
  @Output() onInputSearch = new EventEmitter();
  @Output() onClearChange = new EventEmitter();

  @ViewChild('matMenuTrigger') trigger!: MatMenuTrigger;

  private savedFields: any = [];

  public listFiltersForm!: FormGroup<any>;
  public filterForm!: FormGroup<FilterForm>;
  public hasFilter: boolean = false;
  public filters: any[] = [];
  public currentFilter: any = {};
  public chipName: string = '';
  public isAction = false;
  public isAddFilterClicked = false;
  public isSettingsClicked = false;
  public newFilterName = new FormControl('');
  public notSavedFields: any = [];
  public readonly type = this.route.snapshot.data['type'] as FUNNEL_TYPE;

  public booleanItems: DropdownItem[] = [
    { name: 'Так', value: true },
    { name: 'Ні', value: false },
  ];

  // Окремий тип потрібен через гетер `title`: без нього TS вивів би поле
  // як readonly і `push()` кастомних полів перестав би компілюватись.
  // `title` — гетер, а не значення: масив мутується (`checked`, `push` кастомних
  // полів), тож перебудовувати його на зміну мови не можна. Гетер лишає мутації
  // на місці, але сам заголовок читається з перекладів щоразу.
  private fieldsSettings: FieldSetting[] = [
    {
      get title() {
        return activeTranslations.custom_filters.id;
      },
      key: 'id',
      type: 'uuid',
      checked: false,
    },
    {
      get title() {
        return activeTranslations.custom_filters.name;
      },
      key: 'name',
      type: 'text',
      checked: false,
    },
    {
      get title() {
        return activeTranslations.custom_filters.price;
      },
      key: 'price',
      type: 'price',
      checked: false,
    },
    {
      get title() {
        return activeTranslations.custom_filters.is_unread;
      },
      key: 'is_unread',
      type: 'boolean',
      checked: false,
    },
  ];

  private defaultFields = [
    {
      key: 'name',
      type: 'text',
    },
    {
      key: 'price',
      type: 'price',
    },
    {
      key: 'is_unread',
      type: 'boolean',
    },
  ];

  constructor(
    private readonly modalService: ModalService,
    private readonly dialogService: DialogService,
    private readonly notificationService: NotificationService,
    private readonly crmService: CrmService,
    private readonly route: ActivatedRoute,
  ) {}

  onChange = (data: any) => {};
  onTouch = (_: any) => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(value: string) {
    this.input = value;
    this.onChange(value);
  }

  /**
   *Func triggered on input change
   */
  onchange(event: any) {
    if (this.trigger.menuOpened) {
      this.trigger.closeMenu();
    }
    if (event.trim() !== '' || event === '') {
      this.input = event;
      this.onChange(event);
      this.onValueChange.emit(event);
    }
  }

  search() {
    if (this.input.trim() !== '' || this.input === '') {
      this.onValueChange.emit(this.input);
      this.onInputSearch.emit(this.input);
    }
  }

  clear() {
    this.clearFilters();
    this.onClearChange.emit();
    this.onchange('');
  }

  ngOnInit(): void {
    this.pushCustomFields();
    this.getFilters();
    this.setFields();
    this.listFiltersForm = new FormGroup({});
  }

  private setFields(fields: any[] = [...this.defaultFields]) {
    this.savedFields = [...fields];
    this.notSavedFields = [...this.savedFields];
    this.onInitForm();
  }

  public setDefaultFields() {
    this.notSavedFields = [...this.savedFields];
    this.onInitForm();
  }

  private updateFieldsSettingsCheckedStatus() {
    this.fieldsSettings.forEach((field) => {
      field.checked = this.notSavedFields.some((notSavedField: any) => notSavedField.key === field.key);
    });
  }

  public openModal() {
    this.updateFieldsSettingsCheckedStatus();

    const dialogData = {
      title: 'filter_fields_settings',
      translate: true,
      component: AddFieldFilterComponent,
      width: '50vw',
      data: {
        fieldsSettings: this.fieldsSettings,
      },
    };

    this.modalService.openModal(dialogData).subscribe({
      next: (res) => {
        if (res && Array.isArray(res.selectedFields)) {
          this.notSavedFields = res.selectedFields.map((field: any) => ({
            key: field.key,
            type: field.type,
          }));
          this.onInitForm();
        }
      },
    });
  }

  private pushCustomFields() {
    this.customFields.forEach((field: any) => {
      let newType = field.type;
      if (field.type === 'link' || field.type === 'text' || field.type === 'textarea') {
        newType = 'text';
      }
      if (field.type === 'checkbox') {
        newType = 'boolean';
      }
      this.fieldsSettings.push({
        title: field.title,
        key: field.key,
        type: newType,
        checked: false,
      });
    });
  }

  private onInitForm(): void {
    const fields: any = {};
    this.notSavedFields.forEach((field: any) => {
      fields[field.key] = new FormControl(field.value ? field.value : null);
    });
    this.filterForm = new FormGroup<any>({
      ...fields,
    });
  }

  public changeCurrentFilter(filter: any, filterDeleted: any = false) {
    if (!this.isSettingsClicked || filterDeleted) {
      if (filter.id === this.currentFilter.id) {
        this.currentFilter = {};
        this.setFields();
      } else {
        this.currentFilter = filter;
        this.setFields(filter.x_data);
      }
      this.onInitForm();
    }
  }

  public filterSearch() {
    const rawFilter = this.filterForm.getRawValue();
    const filter = Object.keys(rawFilter).map((key) => {
      return {
        key: key,
        type: this.getFilterType(key),
        value: rawFilter[key],
        is_custom: this.customFields.some((field: any) => field.key === key),
      };
    });
    this.onFilterSearch.emit(filter.filter((v) => v.value || isBoolean(v.value) || isNumber(v.value)));
    this.chipName = this.currentFilter?.name ?? activeTranslations.filters;
    this.hasFilter = true;
    this.trigger.closeMenu();
  }

  public clearFilters() {
    this.onFilterSearch.emit([]);
    this.chipName = '';
    this.hasFilter = false;
  }

  private getFilterType(key: string): string {
    const field = this.notSavedFields.find((field: any) => field.key === key);
    if (field) {
      if (['text'].includes(field.type)) {
        return 'ilike';
      } else if (['price', 'number', 'uuid'].includes(field.type)) {
        return 'eq';
      } else if (['boolean'].includes(field.type)) {
        return 'is';
      }
    }
    return 'unknown';
  }

  public filterReset() {
    if (Object.keys(this.currentFilter).length) {
      const fields: any = {};
      this.currentFilter.x_data.forEach((field: any) => {
        fields[field.key] = new FormControl(field.value ? field.value : null);
      });
      const form = new FormGroup<any>({
        ...fields,
      });
      this.filterForm.reset(form.value);
    } else {
      this.filterForm.reset();
    }
  }

  private getFilters() {
    this.crmService.getAllFilters(this.type).subscribe({
      next: (res) => {
        this.filters = res;
      },
      error: () => {},
    });
  }

  private savedFilter() {
    const filterData = this.filterForm.getRawValue();
    return Object.keys(filterData).map((key) => {
      return {
        key: key,
        type: this.notSavedFields.find((v: any) => v.key === key)?.type,
        value: filterData[key],
      };
    });
  }

  public saveFilter() {
    this.currentFilter.x_data = this.savedFilter();
    this.crmService.updateFilter(this.currentFilter).subscribe({
      next: () => {
        this.notificationService.showSuccess(
          '',
          activeTranslations.notifications_texts.success.successfully_saved,
        );
      },
      error: () => {
        this.notificationService.showError(
          '',
          activeTranslations.notifications_texts.error.error_saving,
        );
      },
    });
  }

  public onAddFilterClick() {
    this.isAction = true;
    this.isAddFilterClicked = true;
  }

  public saveNewFilter() {
    const newFilterName = this.newFilterName.getRawValue();
    if (newFilterName?.trim()) {
      const filter = this.savedFilter();
      const payload = {
        name: newFilterName,
        type: this.type,
        is_shared: false,
        x_data: filter,
      };
      this.crmService.saveFilter(payload).subscribe({
        next: (res) => {
          this.getFilters();
          this.changeCurrentFilter(res);
          this.notificationService.showSuccess(
            '',
            activeTranslations.notifications_texts.success.successfully_created,
          );
        },
        error: () => {
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.error_saving,
          );
        },
      });
      this.cancelAddFilter();
    }
  }

  public cancelAddFilter() {
    this.isAction = false;
    this.isAddFilterClicked = false;
    this.newFilterName = new FormControl('');
  }

  public onSettingsClick() {
    this.isAction = true;
    this.isSettingsClicked = true;
    this.initSettingsForm();
  }

  private initSettingsForm() {
    const controls: any = {};
    this.filters.forEach((filter: any) => {
      controls[filter.id] = new FormControl(filter.name);
    });
    this.listFiltersForm = new FormGroup(controls);
  }

  public saveSettings() {
    const updatedFilters = this.filters
      .map((filter) => {
        const updatedName = this.listFiltersForm.get(filter.id)?.value;
        return { ...filter, name: updatedName };
      })
      .filter((updatedFilter) => {
        const originalFilter = this.filters.find((f) => f.id === updatedFilter.id);
        return originalFilter && originalFilter.name !== updatedFilter.name;
      });
    const updateObservables = updatedFilters.map((filter: any) => {
      this.crmService.updateFilter(filter).subscribe({
        next: () => {},
      });
    });
    combineLatest({ updateObservables }).subscribe({
      next: () => {
        this.getFilters();
        this.notificationService.showSuccess(
          '',
          activeTranslations.notifications_texts.success.successfully_saved,
        );
      },
      error: () => {
        this.notificationService.showError(
          '',
          activeTranslations.notifications_texts.error.error_saving,
        );
      },
    });

    this.cancelSettings();
  }

  public cancelSettings() {
    this.isAction = false;
    this.isSettingsClicked = false;
  }

  public onDeleteFilter(filter: any) {
    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.funnel_filter',
    });

    dialogRef.subscribe((result) => {
      if (result) {
        this.crmService.deleteFilter(filter.id).subscribe({
          next: () => {
            if (filter.id === this.currentFilter.id) {
              this.changeCurrentFilter(filter, true);
            }
            this.getFilters();
            this.notificationService.showSuccess(
              '',
              activeTranslations.notifications_texts.success.successfully_deleted,
            );
          },
          error: () => {
            this.notificationService.showError(
              '',
              activeTranslations.notifications_texts.error.error_while_delete_1,
            );
          },
        });
      }
    });
  }

  public createLabel(fieldKey: any) {
    let label = activeTranslations.custom_filters[fieldKey];
    if (!label) {
      const customField = this.customFields.find((field: any) => field.key === fieldKey);
      if (customField && customField.title) {
        label = customField.title;
      } else {
        label = fieldKey;
      }
    }
    return label;
  }

  protected readonly Object = Object;
}
